/**
 * Decodificador de los códigos de la nota del LSO — Fase 5g.4.
 *
 * El `comment` de un evento `landing quality mark` que emite DCS
 * Supercarrier tiene esta forma:
 *
 *   "LSO: GRADE:OK : _DRX_ _LURX_ (LURIM) _LOIM_ LOIC WIRE# 3"
 *
 * Tras `GRADE:<X>` vienen los códigos posicionales de desviación y, si
 * hubo trap, `WIRE# <N>`. Cada código combina:
 *  - una DESVIACIÓN — qué hizo mal el piloto (bajo, lento, desalineado…),
 *  - una FASE del approach — dónde (al inicio, en el medio, en corto,
 *    en la rampa, en la toma).
 *
 * La TIPOGRAFÍA marca la gravedad con la que el LSO lo anotó (notas 1-2
 * del resumen oficial de DCS):
 *  - `_X_`  subrayado  → grave   (Nota 2: énfasis, "muy")
 *  - `MAYÚS`           → moderada
 *  - `(x)`  paréntesis → leve    (Nota 1: "un poco")
 *  - `minús`          → trivial
 *
 * Y los `[corchetes]` (Nota 3) indican que esa señal del LSO NO fue
 * respondida: `[BC]` = el piloto no cantó la bola.
 *
 * `decodeLsoComment` traduce ese galimatías a texto legible. Los códigos
 * que no están en el diccionario se marcan como NO decodificados — no se
 * inventa su significado (fallback honesto).
 *
 * `DEVIATIONS` y `POSITIONS` reproducen el catálogo COMPLETO del resumen
 * oficial de DCS (`DCS_LSO_Grades.pdf`). Si DCS emite algún código fuera
 * de esa hoja, cae en el fallback honesto. Ampliar es seguro: basta
 * añadir entradas.
 */

/** Gravedad de un código, deducida de su tipografía en el comentario. */
export type LsoSeverity = 'major' | 'moderate' | 'minor' | 'trivial';

/** Fase del approach a la que pertenece un código. */
export type LsoPhase =
  | 'start'
  | 'middle'
  | 'close'
  | 'ramp'
  | 'touchdown'
  | 'general';

/** Orden de presentación de las fases (del inicio del approach a la toma). */
export const LSO_PHASE_ORDER: readonly LsoPhase[] = [
  'start',
  'middle',
  'close',
  'ramp',
  'touchdown',
  'general',
];

/** Etiqueta legible de cada fase del approach. */
export const LSO_PHASE_LABELS: Readonly<Record<LsoPhase, string>> = {
  start: 'Inicio del approach',
  middle: 'En el medio',
  close: 'En corto',
  ramp: 'En la rampa',
  touchdown: 'En la toma / cables',
  general: 'General',
};

/** Etiqueta legible de cada nivel de gravedad. */
export const LSO_SEVERITY_LABELS: Readonly<Record<LsoSeverity, string>> = {
  major: 'Grave',
  moderate: 'Moderada',
  minor: 'Leve',
  trivial: 'Mínima',
};

/** Un código del comentario LSO ya decodificado. */
export interface DecodedLsoCode {
  /** Código sin el envoltorio de gravedad, p.ej. `LOIC`. */
  raw: string;
  /** Gravedad deducida de la tipografía del código. */
  severity: LsoSeverity;
  /** Fase del approach (de la posición del código, o `general`). */
  phase: LsoPhase;
  /** Texto humano del código. `null` si la desviación no se reconoció. */
  text: string | null;
  /** `true` si la desviación se reconoció; `false` → fallback honesto. */
  decoded: boolean;
}

/** Resultado de decodificar un comentario LSO completo. */
export interface DecodedLso {
  /** Calificación general (`GRADE:<X>`), o `null` si no aparece. */
  grade: string | null;
  /** Cable enganchado (`WIRE# <N>`), o `null`. */
  wire: number | null;
  /** Códigos de desviación en orden de aparición. */
  codes: DecodedLsoCode[];
  /**
   * Explicación en prosa que acompaña a las notas SIN calificación —
   * hoy solo los `NC`, donde DCS escribe `GRADE: NC : No proper
   * communications`. `null` en las notas normales, cuyo cuerpo son
   * códigos y no texto.
   *
   * Existe para que el cuerpo del NC NO se trocee como si fueran códigos
   * de desviación: "No", "proper" y "communications" no lo son.
   */
  note: string | null;
}

interface PositionDef {
  phase: LsoPhase;
  /** Frase para componer el texto, p.ej. `en corto`. */
  phrase: string;
}

/**
 * Sufijos de POSICIÓN — dónde ocurrió la desviación ("distance marks" del
 * resumen oficial de DCS). Se buscan como sufijo del código (2 caracteres
 * antes que 1). `X` es el único de un solo carácter.
 */
const POSITIONS: Readonly<Record<string, PositionDef>> = {
  BC: { phase: 'start', phrase: 'al cantar la bola' },
  X: { phase: 'start', phrase: 'al inicio del approach' },
  IM: { phase: 'middle', phrase: 'en el medio' },
  IC: { phase: 'close', phrase: 'en corto' },
  AR: { phase: 'ramp', phrase: 'en la rampa' },
  TL: { phase: 'touchdown', phrase: 'entre la rampa y el primer cable' },
  IW: { phase: 'touchdown', phrase: 'en los cables' },
  AW: { phase: 'touchdown', phrase: 'pasados los cables' },
};

/**
 * DESVIACIONES — qué hizo mal el piloto. Lista COMPLETA de los "main
 * errors" del resumen oficial de DCS (`DCS_LSO_Grades.pdf`). Cualquier
 * código que DCS emita fuera de esta tabla cae en el fallback honesto.
 *
 * `B` (bolter) NO está aquí: es una CALIFICACIÓN, no una desviación —
 * `BIW` se reconoce como caso especial en `decodeToken`.
 */
const DEVIATIONS: Readonly<Record<string, string>> = {
  AFU: 'Todo descontrolado (all fouled up)',
  DL: 'Derivó a la izquierda',
  DR: 'Derivó a la derecha',
  EG: 'No metió gases tras la toma (eased gun)',
  F: 'Rápido',
  FD: 'Cubierta sucia (fouled deck)',
  H: 'Alto en la senda',
  LL: 'Aterrizó desviado a la izquierda',
  LO: 'Bajo en la senda',
  LR: 'Aterrizó desviado a la derecha',
  LUL: 'Desalineado a la izquierda',
  LUR: 'Desalineado a la derecha',
  N: 'Actitud de morro incorrecta',
  NERD: 'Razón de descenso insuficiente',
  NSU: 'Aproximación no estabilizada (not set up)',
  P: 'Manejo de gases (power)',
  SLO: 'Lento',
  TMRD: 'Razón de descenso excesiva',
  W: 'Alas sin nivelar (wings)',
  LLWD: 'Aterrizó con el ala izquierda baja',
  LRWD: 'Aterrizó con el ala derecha baja',
  LNF: 'Aterrizó de morro primero (landed nose first)',
  '3PTS': 'Aterrizó en tres puntos a la vez',
};

// `\s*` porque DCS escribe `GRADE:OK` pegado en las notas normales pero
// `GRADE: NC : No proper communications` con espacio. Sin tolerarlo, el
// grado de un NC no se reconocía y su `GRADE:` acababa en la lista de
// códigos como "sin decodificar".
const GRADE_RE = /GRADE:\s*(\S+)/i;
const WIRE_RE = /WIRE#\s*(\d+)/i;
const WAVE_OFF_RE = /^WO\(([A-Za-z]+)\)(.*)$/;

/** Grado sin calificación: el cuerpo de la nota es prosa, no códigos. */
const NO_GRADE_COMMS = 'NC';

/**
 * Decodifica el `comment` íntegro de una nota del LSO.
 *
 * @param rawComment Texto crudo del comentario LSO.
 * @returns Grado, cable, la lista de códigos decodificados (con fallback
 *          honesto para los no reconocidos) y, en las notas sin calificar,
 *          su explicación en prosa (`note`).
 */
export function decodeLsoComment(rawComment: string): DecodedLso {
  const raw = rawComment.trim();

  const gradeMatch = GRADE_RE.exec(raw);
  const grade = gradeMatch?.[1] ?? null;
  const wireMatch = WIRE_RE.exec(raw);
  const wire =
    wireMatch?.[1] !== undefined ? Number.parseInt(wireMatch[1], 10) : null;

  // Quitar lo que no son códigos de desviación: el prefijo `LSO:`, el
  // `GRADE:<X>` y el `WIRE# <N>`. Los `[corchetes]` se conservan: marcan
  // una señal no respondida y los interpreta `classifyToken`.
  const body = raw
    .replace(/^\s*LSO:\s*/i, ' ')
    .replace(/GRADE:\s*\S+/i, ' ')
    .replace(/WIRE#\s*\d+/gi, ' ');

  // Un NC no lleva códigos: lleva el motivo escrito en inglés llano
  // (`: No proper communications`). Tokenizarlo produciría tres falsos
  // "códigos sin decodificar", así que lo devolvemos como prosa.
  if (grade?.toUpperCase() === NO_GRADE_COMMS) {
    const note = body.replace(/^[\s:]+/, '').trim();
    return { grade, wire, codes: [], note: note === '' ? null : note };
  }

  const codes: DecodedLsoCode[] = [];
  for (const token of body.split(/\s+/)) {
    const decoded = decodeToken(token);
    if (decoded) codes.push(decoded);
  }

  return { grade, wire, codes, note: null };
}

/** Decodifica un token suelto, o `null` si no es un código real. */
function decodeToken(token: string): DecodedLsoCode | null {
  const { severity, notAnswered, bare } = classifyToken(token);
  if (bare === '' || bare === ':') return null;

  // Marcador de bolter — `B` aquí NO es una desviación: es la
  // calificación "Bolter". `BIW` ("Bolter In the Wires") se reconoce
  // entero, antes de intentar descomponerlo en desviación + posición.
  if (bare === 'BIW') {
    return {
      raw: bare,
      severity,
      phase: 'touchdown',
      text: withNotAnswered('Botó tras tocar los cables (bolter)', notAnswered),
      decoded: true,
    };
  }

  // Llamada de la bola. Lo habitual es verlo entre corchetes (`[BC]`),
  // que justamente significa que el piloto NO la cantó.
  if (bare === 'BC') {
    return {
      raw: bare,
      severity,
      phase: 'start',
      text: notAnswered ? 'El piloto no cantó la bola' : 'Llamada de la bola',
      decoded: true,
    };
  }

  // Llamada de wave-off: `WO(<causa>)` + posición opcional.
  const wo = WAVE_OFF_RE.exec(bare);
  if (wo) {
    const causeCode = (wo[1] ?? '').toUpperCase();
    const cause =
      causeCode === 'AFU'
        ? 'por error del piloto'
        : causeCode === 'FD'
          ? 'por cubierta sucia'
          : `(${causeCode})`;
    const posDef = POSITIONS[wo[2] ?? ''];
    return {
      raw: bare,
      severity,
      phase: posDef?.phase ?? 'general',
      text: withNotAnswered(
        posDef ? `Wave-off ${cause}, ${posDef.phrase}` : `Wave-off ${cause}`,
        notAnswered,
      ),
      decoded: true,
    };
  }

  const { phase, text, decoded } = decodeCode(bare);
  return {
    raw: bare,
    severity,
    phase,
    text: decoded ? withNotAnswered(text, notAnswered) : text,
    decoded,
  };
}

/** Añade la coletilla de "señal no respondida" (Nota 3) cuando aplica. */
function withNotAnswered(
  text: string | null,
  notAnswered: boolean,
): string | null {
  if (text === null || !notAnswered) return text;
  return `${text} — señal del LSO no respondida`;
}

/**
 * Descompone la tipografía de un código: deduce la gravedad (Notas 1-2)
 * y si la señal quedó sin responder (Nota 3, `[corchetes]`). Devuelve el
 * código "desnudo", sin ninguno de esos envoltorios.
 */
function classifyToken(token: string): {
  severity: LsoSeverity;
  notAnswered: boolean;
  bare: string;
} {
  let t = token;
  let notAnswered = false;
  // Nota 3: [ ] alrededor de un código → señal del LSO no respondida.
  if (t.length >= 2 && t.startsWith('[') && t.endsWith(']')) {
    notAnswered = true;
    t = t.slice(1, -1);
  }
  if (t.length >= 2 && t.startsWith('_') && t.endsWith('_')) {
    return { severity: 'major', notAnswered, bare: t.slice(1, -1) };
  }
  if (t.length >= 2 && t.startsWith('(') && t.endsWith(')')) {
    return { severity: 'minor', notAnswered, bare: t.slice(1, -1) };
  }
  // Sin envoltorio: todo mayúsculas → moderada; con minúsculas → trivial.
  const severity: LsoSeverity =
    t === t.toUpperCase() ? 'moderate' : 'trivial';
  return { severity, notAnswered, bare: t };
}

/** Descompone un código en desviación + posición y lo traduce. */
function decodeCode(bare: string): {
  phase: LsoPhase;
  text: string | null;
  decoded: boolean;
} {
  let devPart = bare;
  let posDef: PositionDef | undefined;

  // La posición es un sufijo: probar 2 caracteres antes que 1.
  if (bare.length > 2 && POSITIONS[bare.slice(-2)]) {
    posDef = POSITIONS[bare.slice(-2)];
    devPart = bare.slice(0, -2);
  } else if (bare.length > 1 && POSITIONS[bare.slice(-1)]) {
    posDef = POSITIONS[bare.slice(-1)];
    devPart = bare.slice(0, -1);
  }

  const phase = posDef?.phase ?? 'general';
  const devText = DEVIATIONS[devPart];
  if (devText === undefined) {
    // Desviación no reconocida → fallback honesto, sin inventar.
    return { phase, text: null, decoded: false };
  }
  const text = posDef ? `${devText}, ${posDef.phrase}` : devText;
  return { phase, text, decoded: true };
}
